// 🖊️ ASSINATURA DIGITAL — orquestrador do cliente (B212 Fatia 3, AI_126)
// Sistema: HEPATO TX RONDÔNIA
// ─────────────────────────────────────────────────────────────
// O navegador NÃO faz criptografia aqui — só orquestra e guarda o
// pdf-com-placeholder durante a autorização no BIRD ID. A montagem do
// PAdES roda no servidor em São Paulo (api/montar-assinatura.js, gru1):
//
//   1. preparar   → manda o PDF, recebe { pdf-com-placeholder, hashB64 }
//   2. enviarHash → injetado pelo chamador (F4 liga ao broker BRy/F1);
//                   recebe o hashB64, devolve o CMS (só o HASH sai à BRy)
//   3. embutir    → manda { pdf-com-placeholder, CMS }, recebe o PDF assinado
//
// LGPD: o conteúdo do paciente NUNCA vai à BRy — só o hash. O PDF nasce e
// fica na nossa infra (render gru1 → este orquestrador → Firestore Brasil).
//
// CFM 2454/2026 Art. 17: POST autenticado (authFetch, ID Token + retry 401).
// ─────────────────────────────────────────────────────────────

import { postAutenticado } from "../../../lib/authFetch";

const ENDPOINT = "/api/montar-assinatura";

/** Metadados do widget de assinatura (reason/name/location aparecem no PDF). */
export interface DadosAssinaturaWidget {
  reason?: string;
  contactInfo?: string;
  name?: string;
  location?: string;
}

/** Função que troca o hash pelo CMS da BRy (injetada pela F4). */
export type EnviarHash = (hashB64: string) => Promise<string>;

export interface ResultadoAssinatura {
  /** PDF assinado (PAdES), base64 — pronto p/ persistir (F5). */
  pdfAssinadoBase64: string;
  /** O hash que efetivamente saiu à BRy (rastreabilidade). */
  hashEnviado: string;
  /** Tamanho do PDF assinado, em bytes. */
  bytes: number;
}

interface RespPreparar {
  pdfComPlaceholderBase64: string;
  hashB64: string;
}

interface RespEmbutir {
  pdfAssinadoBase64: string;
  bytes: number;
}

/**
 * Passo 1 — insere o placeholder PAdES e calcula o hash (no servidor gru1).
 * @returns o pdf-com-placeholder (o navegador o guarda) e o hash a assinar.
 */
export async function prepararAssinatura(
  pdfBase64: string,
  dados: DadosAssinaturaWidget = {}
): Promise<RespPreparar> {
  const r = await postAutenticado<RespPreparar>(
    ENDPOINT,
    { acao: "preparar", pdfBase64, dados },
    "assinaturaCliente.preparar"
  );
  if (!r) throw new Error("Sessão expirada — entre novamente para assinar.");
  return r;
}

/**
 * Passo 3 — encaixa o CMS devolvido pela BRy no pdf-com-placeholder (gru1).
 * @returns o PDF assinado (PAdES) em base64.
 */
export async function embutirAssinatura(
  pdfComPlaceholderBase64: string,
  cmsBase64: string
): Promise<RespEmbutir> {
  const r = await postAutenticado<RespEmbutir>(
    ENDPOINT,
    { acao: "embutir", pdfComPlaceholderBase64, cmsBase64 },
    "assinaturaCliente.embutir"
  );
  if (!r) throw new Error("Sessão expirada — entre novamente para assinar.");
  return r;
}

/**
 * Fluxo hash-only ponta a ponta: prepara → (enviarHash troca hash↔CMS na BRy)
 * → embute. O conteúdo do PDF nunca trafega para fora da nossa infra.
 *
 * @param pdfBase64    PDF binário a assinar (saída do render F2/gerar-pdf).
 * @param enviarHash   troca o hashB64 pelo CMS base64 (F4 liga ao broker BRy).
 * @param dados        metadados do widget (name/reason/location).
 */
export async function assinarPdfPorHash(
  pdfBase64: string,
  enviarHash: EnviarHash,
  dados: DadosAssinaturaWidget = {}
): Promise<ResultadoAssinatura> {
  const { pdfComPlaceholderBase64, hashB64 } = await prepararAssinatura(pdfBase64, dados);

  const cmsBase64 = await enviarHash(hashB64);
  if (!cmsBase64) {
    throw new Error("A BRy não devolveu o CMS (assinatura não concluída).");
  }

  const { pdfAssinadoBase64, bytes } = await embutirAssinatura(pdfComPlaceholderBase64, cmsBase64);
  return { pdfAssinadoBase64, hashEnviado: hashB64, bytes };
}
